import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Play, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { SocketKey } from '@/Config/api/socketKey';
import {
  DatasetDetailDto,
  DEFAULT_SIMULATION_PAGE_SIZE,
  PaginatedResponse,
  RunListDto,
} from '@/Domain/api/Simulation/dto/Simulation.dto';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/Domain/components/Shared/Breadcrumb/Breadcrumb';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/Domain/components/Shared/Tabs/Tabs';
import { useSocket } from '@/Domain/components/SocketProvider';
import { AddMessagesForm } from '@/UI/Simulation/components/AddMessagesForm';
import { ConversationMetadataEditor } from '@/UI/Simulation/components/ConversationMetadataEditor';
import { DeleteDatasetDialog } from '@/UI/Simulation/components/DeleteDatasetDialog';
import { EvaluationCriteriaEditor } from '@/UI/Simulation/components/EvaluationCriteriaEditor';
import { Paginator } from '@/UI/Simulation/components/Paginator';
import { RunCard } from '@/UI/Simulation/components/RunCard';
import { StartRunDialog } from '@/UI/Simulation/components/StartRunDialog';

export type DatasetDetailTab = 'messages' | 'runs';

type Props = {
  datasetId: number;
  initialTab?: DatasetDetailTab;
  onBack: () => void;
  onViewRun: (runId: number) => void;
  onDatasetLoaded?: (name: string) => void;
};

export const DatasetDetailView = ({
  datasetId,
  initialTab = 'messages',
  onBack,
  onViewRun,
  onDatasetLoaded,
}: Props) => {
  const { t } = useTranslation('simulation_page');
  const queryClient = useQueryClient();
  const [showStartRun, setShowStartRun] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState<DatasetDetailTab>(initialTab);
  const [runsOffset, setRunsOffset] = useState(0);
  const runsLimit = DEFAULT_SIMULATION_PAGE_SIZE;

  const { data: dataset, isLoading } = useQuery<DatasetDetailDto>({
    queryKey: [QueryKey.simulation.datasetDetail, datasetId.toString()],
  });
  const { data: runsPage } = useQuery<PaginatedResponse<RunListDto>>({
    queryKey: [QueryKey.simulation.runs, datasetId.toString(), runsOffset, runsLimit],
    refetchInterval: 5000,
  });
  const runs = runsPage?.items;
  const runsTotal = runsPage?.total ?? 0;

  const deleteMutation = useMutation<void, unknown, number>({
    mutationKey: [QueryKey.simulation.deleteDataset],
  });

  const hasActiveRun = runs?.some((r) => r.status === 'running' || r.status === 'pending') ?? false;

  useEffect(() => {
    if (dataset?.name) onDatasetLoaded?.(dataset.name);
  }, [dataset?.name, onDatasetLoaded]);

  // Listen for run progress via WebSocket
  const { socket } = useSocket();
  useEffect(() => {
    const handler = () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.simulation.runs, datasetId.toString()] });
      // The dataset list shows the latest run's avg score; keep it fresh too.
      queryClient.invalidateQueries({ queryKey: [QueryKey.simulation.datasets] });
    };
    socket.on(SocketKey.simulation.runProgress, handler);
    return () => {
      socket.off(SocketKey.simulation.runProgress, handler);
    };
  }, [socket, queryClient, datasetId]);

  const handleDelete = () => {
    deleteMutation.mutate(datasetId, {
      onSuccess: () => {
        toast.success(t('toast_dataset_deleted'));
        setShowDeleteConfirm(false);
        onBack();
      },
    });
  };

  if (isLoading || !dataset) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dataset-detail-view">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink data-testid="dataset-detail-breadcrumb-root" onClick={onBack}>
              {t('datasets_breadcrumb')}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage data-testid="dataset-detail-breadcrumb-current">
              {dataset.name}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 data-testid="dataset-detail-name" className="text-lg font-semibold">
            {dataset.name}
          </h1>
          <Badge data-testid="dataset-detail-count" variant="secondary">
            {t('messages_count', { count: dataset.message_count })}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="dataset-delete-button"
            size="icon"
            variant="ghost"
            onClick={() => setShowDeleteConfirm(true)}
            aria-label={t('delete')}
          >
            <Trash2 className="h-4 w-4 text-muted-foreground" />
          </Button>
          <Button
            data-testid="dataset-run-tests-button"
            size="sm"
            onClick={() => setShowStartRun(true)}
            disabled={!dataset.message_count || hasActiveRun}
          >
            <Play className="mr-1.5 h-3.5 w-3.5" />
            {t('run_tests')}
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as DatasetDetailTab)}>
        <TabsList>
          <TabsTrigger value="messages">{t('tab_messages')}</TabsTrigger>
          <TabsTrigger value="runs">
            {t('tab_runs')} {runsTotal ? `(${runsTotal})` : ''}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="messages" className="mt-4 space-y-4">
          {dataset.messages.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">{t('no_messages')}</p>
          ) : (
            <div className="rounded-md border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="w-12 px-3 py-2 text-left font-medium">#</th>
                    <th className="px-3 py-2 text-left font-medium">{t('column_message')}</th>
                  </tr>
                </thead>
                <tbody>
                  {dataset.messages.map((msg) => (
                    <tr key={msg.id} className="border-b last:border-0 align-top">
                      <td className="px-3 py-2 text-muted-foreground tabular-nums">
                        {msg.ord_number}
                      </td>
                      <td className="whitespace-pre-wrap px-3 py-2">{msg.user_message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <AddMessagesForm datasetId={datasetId} existingCount={dataset.message_count} />
        </TabsContent>

        <TabsContent value="runs" className="mt-4">
          {!runs?.length ? (
            <p className="py-8 text-center text-sm text-muted-foreground">{t('no_runs')}</p>
          ) : (
            <>
              <div className="space-y-2">
                {runs.map((run) => (
                  <RunCard key={run.id} run={run} onClick={() => onViewRun(run.id)} />
                ))}
              </div>
              <Paginator
                testid="runs-paginator"
                offset={runsOffset}
                limit={runsLimit}
                total={runsTotal}
                onChange={setRunsOffset}
              />
            </>
          )}
        </TabsContent>
      </Tabs>

      {activeTab === 'messages' && (
        <>
          <EvaluationCriteriaEditor datasetId={datasetId} criteria={dataset.evaluation_criteria} />
          <ConversationMetadataEditor
            datasetId={datasetId}
            metadata={dataset.conversation_metadata}
          />
        </>
      )}

      <StartRunDialog
        open={showStartRun}
        onOpenChange={setShowStartRun}
        datasetId={datasetId}
        messageCount={dataset.message_count}
        onSuccess={(runId) => {
          setShowStartRun(false);
          queryClient.invalidateQueries({
            queryKey: [QueryKey.simulation.runs, datasetId.toString()],
          });
          onViewRun(runId);
        }}
      />

      <DeleteDatasetDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        onConfirm={handleDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};
